//libs
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
//Components
import { EditIcon, InputNumber, Span } from 'components';
//Hooks
import { useToastMessage } from 'hooks';
//Utils
import { ApiServiceV2 } from 'services';
//Services
import { formatNumber, coinsUtils } from 'utils/libs';
//Action
import PaymentAuthorization from 'modules/Payments/PaymentAuthorization';
//Selectors
import { selectProfile } from 'screens/Login/selectors';
import {
	formatAmountInput,
	isFrozenSerializedAvailability,
} from './Amount.utils';

const CenteredInputNumber = styled(InputNumber)`
	& .ant-input-number-input {
		text-align: center;
	}
`;

const EDITABLE_PAYMENT_ORDER_FIELDS = ['amount', 'charge'];

const Amount = ({ rowData, field, value, stateTable }) => {
	const profile = useSelector(selectProfile);
	const sendToast = useToastMessage();
	const inputRef = useRef(null);

	const [state, _setState] = useState({
		isEditing: false,
		inputValue: null,
	});

	const setState = newState =>
		_setState(current => ({ ...current, ...newState }));
	const setInputValue = newValue => setState({ inputValue: newValue });

	const isPaymentLine = rowData.mainPaymentId !== undefined;
	const isPaymentOrderCharge = !isPaymentLine && field.id === 'charge';
	const isExternalEditMode =
		Boolean(field.editMode) &&
		typeof field.onSubmitEdit === 'function' &&
		(typeof field.isEditable !== 'function' || field.isEditable(rowData));
	const isExternalSubmitting =
		isExternalEditMode &&
		typeof field.isSubmitting === 'function' &&
		field.isSubmitting(rowData);

	const submitAmountChange = () => {
		if (isExternalEditMode) {
			field.onSubmitEdit({
				rowData,
				currentValue: value,
				targetValue: state.inputValue,
			});
			return;
		}
		setState({ isEditing: false });
		if (state.inputValue === null || state.inputValue === undefined) return;

		const apiCall = isPaymentLine
			? ApiServiceV2.put({
					url: `/payments_lines/${rowData.id}/amount`,
					data: {
						newAmount: state.inputValue,
						payment_id: rowData.mainPaymentId,
						paycut_id: rowData.paycut_id,
					},
				})
			: ApiServiceV2.put({
					url: `/payments_orders/${rowData.id}/${
						isPaymentOrderCharge ? 'charge' : 'amount'
					}`,
					data: isPaymentOrderCharge
						? { newCharge: state.inputValue }
						: { newAmount: state.inputValue },
				});

		apiCall
			.then(() => {
				field.refreshRowData?.({
					rowId: rowData.id,
					orderId: rowData.orderId || rowData.order_id,
					...(isPaymentOrderCharge
						? { newCharge: state.inputValue }
						: { newAmount: state.inputValue }),
				});
				sendToast({
					message: isPaymentOrderCharge
						? 'Venta actualizada'
						: 'Precio actualizado',
					type: 'success',
				});
			})
			.catch(err => sendToast({ message: err.message, type: err.type }));
	};

	const toggleEdit = () =>
		_setState(current => ({ ...current, isEditing: !current.isEditing }));

	useEffect(() => {
		if (state.isEditing || isExternalEditMode) setInputValue(value);
		if (state.isEditing) inputRef.current?.focus();
	}, [state.isEditing, isExternalEditMode, value, field.editResetKey]);

	const coinSymbol =
		coinsUtils.getDataByCoinId(
			rowData.price?.coinId || rowData.coin_id,
			'symbol',
			rowData.coinsData,
		) || rowData.coin_symbol;

	const formattedRawValue = formatNumber.new(value, coinSymbol || '', 2);
	const displayValue = isFrozenSerializedAvailability({
		rowData,
		stateTable,
		field,
	})
		? '-'
		: formattedRawValue;
	const NumericInput = field.centerContent ? CenteredInputNumber : InputNumber;
	const valueStyle = field.centerContent
		? { display: 'block', textAlign: 'center', width: '100%' }
		: undefined;
	const isClickable =
		typeof field.onClick === 'function' &&
		(typeof field.isClickable !== 'function' || field.isClickable(rowData));
	return (
		<>
			{state.isEditing || isExternalEditMode ? (
				<NumericInput
					ref={inputRef}
					style={{ width: '100%' }}
					value={state.inputValue}
					min={field.minValue}
					precision={field.precision}
					disabled={isExternalSubmitting}
					formatter={value => formatAmountInput(value, coinSymbol)}
					onPressEnter={submitAmountChange}
					onBlur={() => {
						if (isExternalEditMode) return;
						setTimeout(() => setState({ isEditing: false }), 0);
					}}
					onChange={setInputValue}
				/>
			) : (
				<Span
					style={valueStyle}
					emphasis
					hoverable={isClickable}
					cursor={isClickable ? 'pointer' : undefined}
					maxHeight='200px'
					overflow='auto'
					onClick={
						isClickable
							? event => {
									event.stopPropagation();
									field.onClick(rowData, event);
								}
							: undefined
					}
				>
					{displayValue}
				</Span>
			)}

			{!isExternalEditMode &&
				!field.disableEdit &&
				field.canManageOrders !== false &&
				!isPaymentLine &&
				typeof rowData.id === 'number' &&
				new PaymentAuthorization(profile).checkEditOrderAmount() &&
				EDITABLE_PAYMENT_ORDER_FIELDS.includes(field.id) && (
					<EditIcon onClick={toggleEdit} />
				)}
		</>
	);
};

export default Amount;
